import React, { useEffect, useRef, useState } from "react";
import {
  getAthletePositionDrillsAction,
  setProfileAction,
} from "../../../store/AthleteActions";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../../../services/Interceptor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faDownload,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../LoadingSpinner";
import VideoPlayer from "../VideoPlayer";
import CoachingTipsModal from "../../CoachingTipsModal";
import PremiumFeatureModal from "../../PremiumFeatureModal";
import FreeTrialModal from "../../FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import {
  clearRegisterData,
  registerAccountStore,
} from "../../../store/slices/accountSlice";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";
import { toast } from "react-toastify";
import { useSubscriptionAccess } from "../../../hooks/useSubscriptionAccess";

export default function PositionDrills({ userId }) {
  const dispatch = useDispatch();
  const { positionDrill, loading } = useSelector((state) => state.athletes);
  const apiInstance = useAxiosInstance();

  const { user, hasSubscriptionAccess, showFreeTrial } =
    useSubscriptionAccess();

  const [data, setData] = useState([]);
  const [openIndexes, setOpenIndexes] = useState([]);
  const [openDrillIndexes, setOpenDrillIndexes] = useState({});
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const [viewingTip, setViewingTip] = useState(null);
  const videoRefsMap = useRef({});
  const openIndexesRef = useRef([]);
  const openDrillIndexesRef = useRef({});

  useEffect(() => {
    dispatch(getAthletePositionDrillsAction(apiInstance, userId));
  }, [dispatch, apiInstance, userId]);

  useEffect(() => {
    if (!positionDrill || positionDrill.length === 0) {
      setData([]);
      setOpenIndexes([]);
      setOpenDrillIndexes({});
      return;
    }

    const drillMap = new Map();

    positionDrill.forEach((event) => {
      event.drills.forEach((drill) => {
        const position = drill.positionName;
        if (!drillMap.has(position)) {
          drillMap.set(position, new Map());
        }

        const positionDrills = drillMap.get(position);

        if (!positionDrills.has(drill.drillName)) {
          positionDrills.set(drill.drillName, {
            drillName: drill.drillName,
            firstVideo: drill.videos[0] || null,
            events: [],
          });
        }

        const repMap = new Map();
        drill.videos.forEach((video) => {
          if (!repMap.has(video.repNumber)) {
            repMap.set(video.repNumber, []);
          }
          repMap.get(video.repNumber).push({
            filePath: video.filePath,
            angle: video.angle,
            coachingTip: video.coachingTip || "",
          });
        });

        positionDrills.get(drill.drillName).events.push({
          name: event.eventName,
          date: event.eventDate,
          reps: Array.from(repMap, ([rep, videos]) => ({ rep, videos })),
        });
      });
    });

    const result = Array.from(drillMap.entries()).map(
      ([position, drillsMap]) => [position, Array.from(drillsMap.values())],
    );

    setData(result);
  }, [positionDrill]);

  useEffect(() => {
    openIndexesRef.current = openIndexes;
  }, [openIndexes]);

  useEffect(() => {
    openDrillIndexesRef.current = openDrillIndexes;
  }, [openDrillIndexes]);

  const registerVideoRef = (key, element) => {
    if (element) {
      videoRefsMap.current[key] = element;
    } else {
      delete videoRefsMap.current[key];
    }
  };

  const pauseVideosMatching = (prefix) => {
    Object.keys(videoRefsMap.current).forEach((key) => {
      if (key.startsWith(prefix)) {
        videoRefsMap.current[key]?.pause();
      }
    });
  };

  const toggleAccordion = (index) => {
    if (openIndexesRef.current.includes(index)) {
      pauseVideosMatching(`${index}-`);
    }
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const toggleDrill = (posIndex, drillIndex) => {
    const key = `${posIndex}-${drillIndex}`;
    if (openDrillIndexesRef.current[key]) {
      pauseVideosMatching(`${posIndex}-${drillIndex}-`);
    }
    setOpenDrillIndexes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const openSubscription = () => {
    const updatedData = {
      userId: user.id,
      role: "athlete",
      email: user.email,
      registered: true,
      eventId: 0,
    };
    dispatch(clearRegisterData());
    dispatch(registerAccountStore(updatedData));
    localStorage.setItem("registeredAccount", JSON.stringify(updatedData));
    localStorage.removeItem("subscriptionDetails");
    setTimeout(() => {
      window.location.href = "/register/subscription";
    }, 2000);
  };

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const handleDownload = async (filePath, fileName, downloadKey) => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    try {
      setDownloadingIndex(downloadKey);

      const response = await fetch(filePath);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();

      URL.revokeObjectURL(url);
    } catch {
      toast.error("Failed to download video. Please try again.", {
        position: "top-right",
      });
    } finally {
      setDownloadingIndex(null);
    }
  };

  return (
    <div className="pt-0 px-4 -m-4 mt-0 overflow-y-auto pb-2 xl:pb-0 lg:pb-0">
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Video download is locked. Start a free trial or subscribe now to download videos."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <CoachingTipsModal
        isOpen={viewingTip !== null}
        title="View Coaching Tips"
        description={null}
        coachingTip={viewingTip || ""}
        readOnly={true}
        onClose={() => setViewingTip(null)}
      />
      {loading ? (
        <LoadingSpinner />
      ) : data.length > 0 ? (
        <div className="mt-3">
          {data.map(([positionName, drills], posIndex) => (
            <div key={posIndex} className="mb-2">
              <div className="border rounded shadow-sm space-y-2">
                <button
                  onClick={() => toggleAccordion(posIndex)}
                  className="flex justify-between items-center w-full px-4 py-3 font-semibold bg-gray-100 hover:bg-gray-200 focus:outline-none"
                >
                  {positionName}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`transition-transform duration-300 ${
                      openIndexes.includes(posIndex) ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {openIndexes.includes(posIndex) && (
                <div>
                  {drills.map((drill, drillIndex) => {
                    const drillKey = `${posIndex}-${drillIndex}`;
                    const isDrillOpen = openDrillIndexes[drillKey];

                    return (
                      <div key={drillIndex} className="border shadow-sm mb-6">
                        <button
                          onClick={() => toggleDrill(posIndex, drillIndex)}
                          className="flex justify-between items-center w-full px-4 py-2 bg-white border-l-4 border-blue-600 hover:bg-gray-100 focus:outline-none"
                        >
                          <h3 className="text-lg font-bold text-blue-800">
                            Drill: {drill.drillName}
                          </h3>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`transition-transform duration-300 ${
                              isDrillOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isDrillOpen && (
                          <div className="p-0">
                            {drill.events.map((event, eventIndex) => (
                              <div key={eventIndex}>
                                <div className="font-bold bg-gray-200 p-2">
                                  {event.name} - {event.date}
                                </div>
                                {event.reps.map((rep, repIndex) => {
                                  const repTip = rep.videos.find((v) =>
                                    v.coachingTip?.trim(),
                                  )?.coachingTip;
                                  return (
                                    <div key={repIndex}>
                                      <div className="bg-gray-100 p-2 font-bold">
                                        Rep {rep.rep}
                                      </div>
                                      <div className="flex flex-wrap space-x-4 mt-2">
                                        {rep.videos.map((vid, vidIndex) => (
                                          <div key={vidIndex} className="p-3">
                                            {vid.filePath ? (
                                              <VideoPlayer
                                                src={vid.filePath}
                                                registerVideoRef={(el) =>
                                                  registerVideoRef(
                                                    `${posIndex}-${drillIndex}-${eventIndex}-${repIndex}-${vidIndex}`,
                                                    el,
                                                  )
                                                }
                                              />
                                            ) : (
                                              <div className="text-gray-500">
                                                No video available
                                              </div>
                                            )}
                                            <div className="mt-2 flex items-center justify-between">
                                              <span className="text-base text-gray-800 truncate max-w-[200px]">
                                                {vid.angle}
                                              </span>
                                              {vid.filePath && (
                                                <button
                                                  onClick={() =>
                                                    handleDownload(
                                                      vid.filePath,
                                                      `Drill_${drill.drillName}_Rep${rep.rep}_${vid.angle}.mp4`,
                                                      `${eventIndex}-${repIndex}-${vidIndex}`,
                                                    )
                                                  }
                                                >
                                                  {downloadingIndex ===
                                                  `${eventIndex}-${repIndex}-${vidIndex}` ? (
                                                    <FontAwesomeIcon
                                                      icon={faSpinner}
                                                      spin
                                                    />
                                                  ) : (
                                                    <FontAwesomeIcon
                                                      icon={faDownload}
                                                    />
                                                  )}
                                                </button>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      {repTip && (
                                        <button
                                          type="button"
                                          onClick={() => setViewingTip(repTip)}
                                          className="flex items-center mt-2 mb-1 px-3 text-sm font-medium text-gray-600 hover:text-blue-800 hover:underline"
                                        >
                                          <img
                                            src="/viewnote.svg"
                                            alt="View Tips"
                                            className="h-5 pr-2"
                                          />
                                          View Coaching Tips
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="p-3 text-blue-900 bg-blue-100 border border-blue-300">
          There are no Position Drills added.
        </div>
      )}
    </div>
  );
}
